import React, { useEffect, useRef, useState } from "react";
import { Button, Spinner } from "@heroui/react";
import { FiPaperclip, FiSend, FiX, FiFile, FiDownload } from "react-icons/fi";
import { useChat } from "@/hooks/useChat";
import { ChatMessage } from "@/types/chat";

// The message stream plus composer for one participant's thread, usable
// anywhere (the ChatWindow modal wraps it; the cohort Messages page embeds it
// inline). The parent controls the height via `className`.
interface ChatConversationProps {
  cohortId?: string;
  cohortName?: string;
  participantEmail?: string;
  participantName?: string;
  // Streams messages and clears the unread badge only while true (mirrors the
  // old modal's isOpen behavior).
  active: boolean;
  className?: string;
}

const formatSize = (bytes: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTime = (date: Date | null) => {
  if (!date) return "Sending…";
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  return sameDay ? time : `${date.toLocaleDateString()} ${time}`;
};

const ChatConversation: React.FC<ChatConversationProps> = ({
  cohortId,
  cohortName,
  participantEmail,
  participantName,
  active,
  className = "h-full",
}) => {
  const {
    thread,
    messages,
    loadingMessages,
    sending,
    error,
    isStaff,
    sendMessage,
    clearError,
  } = useChat({
    cohortId,
    cohortName,
    participantEmail,
    participantName,
    isOpen: active,
  });

  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view.
  useEffect(() => {
    if (!active) return;
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, active]);

  // Don't leak a half-written draft from one participant into another.
  useEffect(() => {
    setText("");
    setFiles([]);
  }, [participantEmail]);

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length) setFiles((prev) => [...prev, ...selected]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = async () => {
    if (sending) return;
    const ok = await sendMessage(text, files);
    if (ok) {
      setText("");
      setFiles([]);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter adds a newline.
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const isMine = (message: ChatMessage) =>
    isStaff ? message.senderRole === "staff" : message.senderRole !== "staff";

  // Read receipt for the newest message when it's ours: the other side's
  // unread counter is cleared the moment they open the thread, so counter > 0
  // means our latest message is still sitting unread.
  const lastMessage = messages[messages.length - 1];
  const otherSideUnread = thread
    ? isStaff
      ? thread.unreadForParticipant
      : thread.unreadForStaff
    : 0;
  const readReceipt =
    lastMessage && isMine(lastMessage)
      ? otherSideUnread > 0
        ? "awaiting"
        : "read"
      : null;

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-3">
        {loadingMessages && messages.length === 0 ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : messages.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <p className="font-medium text-gray-700">No messages yet</p>
            <p className="text-sm mt-1">
              Send the first message to start the conversation.
            </p>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            {messages.map((message) => {
              const mine = isMine(message);
              return (
                <div
                  key={message.id}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                      mine
                        ? "bg-blue-600 text-white rounded-br-sm"
                        : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"
                    }`}
                  >
                    {!mine && (
                      <p className="text-xs font-semibold text-gray-500 mb-1">
                        {message.senderName || "Trainer"}
                      </p>
                    )}
                    {message.text && (
                      <p className="whitespace-pre-wrap break-words text-sm">
                        {message.text}
                      </p>
                    )}

                    {message.attachments.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {message.attachments.map((file) => (
                          <a
                            key={file.url}
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm ${
                              mine
                                ? "bg-blue-800/60 hover:bg-blue-800 !text-white"
                                : "bg-gray-100 hover:bg-gray-200 !text-gray-700"
                            }`}
                          >
                            <FiFile className="shrink-0" />
                            <span className="truncate flex-1">{file.name}</span>
                            <span
                              className={`text-xs ${
                                mine ? "text-blue-100" : "text-gray-500"
                              }`}
                            >
                              {formatSize(file.size)}
                            </span>
                            <FiDownload className="shrink-0" />
                          </a>
                        ))}
                      </div>
                    )}

                    <p
                      className={`mt-1 text-[11px] ${
                        mine ? "text-blue-100" : "text-gray-400"
                      }`}
                    >
                      {formatTime(message.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
            {readReceipt && (
              <div className="flex justify-end pr-1">
                <span
                  className={`text-[11px] font-medium ${
                    readReceipt === "awaiting"
                      ? "text-amber-600"
                      : "text-gray-400"
                  }`}
                >
                  {readReceipt === "awaiting"
                    ? "Delivered — not read yet"
                    : "Read"}
                </span>
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <div className="flex flex-col items-stretch gap-2 border-t border-gray-200 bg-white p-3">
        {error && (
          <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <span>{error}</span>
            <button type="button" onClick={clearError} aria-label="Dismiss">
              <FiX />
            </button>
          </div>
        )}

        {files.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {files.map((file, index) => (
              <span
                key={`${file.name}-${index}`}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700"
              >
                <FiFile />
                <span className="max-w-[160px] truncate">{file.name}</span>
                <span className="text-gray-400">{formatSize(file.size)}</span>
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() =>
                    setFiles((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  <FiX />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          <Button
            isIconOnly
            variant="flat"
            aria-label="Attach files"
            title="Attach files"
            onPress={() => fileInputRef.current?.click()}
            isDisabled={sending}
          >
            <FiPaperclip />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            aria-label="Choose files to attach"
            onChange={handleFiles}
          />

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            placeholder="Write a message…  (Enter to send, Shift+Enter for a new line)"
            aria-label="Message"
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <Button
            color="primary"
            className="bg-blue-600"
            isLoading={sending}
            isDisabled={!text.trim() && files.length === 0}
            onPress={handleSend}
            startContent={!sending ? <FiSend /> : undefined}
          >
            Send
          </Button>
        </div>
        <p className="text-xs text-gray-400">
          Files up to 10MB. Everyone in this conversation can download them.
        </p>
      </div>
    </div>
  );
};

export default ChatConversation;
